import React from 'react';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import { CleaveOptions } from 'cleave.js/options';

import BaseInputWrapper from '../BaseInputWrapper';
import useDefaultInputHook from '../hooks/useDefaultInputHook';
import InputProps from '../InputProps';
import { MaskedCustom, generateProperties } from '../helpers';
import { CustomColor } from '../hooks/useTextInputStyles';
import { colors } from '../../../theme';

interface Props extends InputProps<string> {
  /** What input type the text field accepts (defaults to text) */
  type?: string;
  /** A placeholder to show while no text is available */
  placeholder?: string;
  /** A middleware that, if defined, will decide if the onChange method will be called */
  shouldUpdate?: (newVal: string) => boolean;
  /** Apply custom logic when user focuses on the input */
  customFocus?: () => void;
  /** Apply custom logic when user gets out of focus from the input */
  customBlur?: () => void;
  /** A custom mask to apply to the text field (could have conflicts with shouldUpdate) */
  maskOptions?: CleaveOptions;
  /** If true, it will show a loading in place of the decoration */
  loading?: boolean;
  /** Custom colors that will overwrite how the input is rendered */
  customColor?: CustomColor;
  /** If defined, will overwrite the current decoration */
  customDecoration?: React.ReactNode;
  /** Custom style, will use in the input material ui component */
  style?: React.CSSProperties;
}

const DEFAULT_COLORS: CustomColor = {
  font: colors.madison,
  border: colors.silver,
  icon: colors.white,
};

const TextField = ({
  inputId,
  inputName,
  inputLabel,
  onChange,
  customFocus,
  customBlur,
  maskOptions = null,
  defaultValue = '',
  type = 'text',
  placeholder = '',
  inputError = false,
  inputErrorMessage = '',
  helperText,
  isDisabled = false,
  dataSource,
  shouldUpdate,
  loading = false,
  customColor = DEFAULT_COLORS,
  customDecoration,
  style,
}: Props): JSX.Element => {
  const formInputProps = generateProperties<string>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });
  const { currentClass, decoration, onBlur, onFocus } = useDefaultInputHook({
    error: formInputProps.error,
    defaultValue: formInputProps.value,
    customColor,
    loading,
    customFocus,
    customBlur,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value;
    if (shouldUpdate && !shouldUpdate(newValue)) {
      return;
    }
    formInputProps.onChange(newValue);
  };

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={formInputProps.error}
      errorMessage={formInputProps.errorText}
      disabled={isDisabled}
      helperText={helperText}
      colorLabel={customColor.font}
    >
      <Input
        id={inputId}
        name={formInputProps.name}
        value={formInputProps.value}
        className={currentClass}
        disabled={isDisabled}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        style={style}
        type={type}
        placeholder={placeholder}
        endAdornment={
          <InputAdornment position="end">
            {typeof customDecoration !== 'undefined'
              ? customDecoration
              : decoration}
          </InputAdornment>
        }
        inputComponent={maskOptions === null ? 'input' : MaskedCustom}
        inputProps={
          maskOptions === null
            ? {}
            : {
                maskOptions: {
                  ...maskOptions,
                  id: inputId,
                  disabled: isDisabled,
                },
              }
        }
      />
    </BaseInputWrapper>
  );
};

export default TextField;
